import { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { drawableAreas, type BuySearchMapArea } from './buySearchAreas';

/**
 * The Buying tab's map (BUY1): ONE BUBBLE PER AREA, never a pin per building.
 *
 * ⛔ Its only input is the contract's list of areas - a name, a fixed named place to draw it on, and a count. It is never
 * handed a building, a parcel, a coordinate of a building, or `parcels_geojson`, and it reads nothing off the page. An
 * area's center is a place the contract names (a city or district center), never computed from the buildings in it, so a
 * bubble cannot sit on the one building in its area. The map stops at zoom 12 so that zooming in shows nothing the
 * bubbles do not: at that scale a warehouse is a few pixels.
 *
 * An area named only by buildings that have left the search arrives with `count: 0` and is not drawn
 * (`drawableAreas`, in buySearchAreas.ts, is the rule the tab shares).
 */

/** The farthest in the map zooms. 12 is a city; the imagery at this scale cannot show a building. */
export const AREA_MAP_MAX_ZOOM = 12;

/** The words on a bubble. */
export const bubbleText = (area: Pick<BuySearchMapArea, 'label' | 'count'>): string => `${area.label}: ${area.count}`;

/** A bubble's radius in pixels: bigger for more buildings, but a one-building area is still easy to tap. */
export const bubbleRadius = (count: number): number => Math.round(Math.min(30, 11 + 4 * Math.sqrt(Math.max(count, 0))));

/** The map fits the area centers and nothing else. Null when there is nothing to fit. */
export const boundsOfAreas = (areas: readonly BuySearchMapArea[]): L.LatLngBounds | null =>
  areas.length ? L.latLngBounds(areas.map((a) => [a.center.lat, a.center.lng] as [number, number])) : null;

const BUBBLE_COLOR = '#b01e28'; // the Apex red

export default function BuySearchAreaMap({ areas }: { areas: BuySearchMapArea[] }) {
  const drawn = useMemo(() => drawableAreas(areas), [areas]);
  const bounds = useMemo(() => boundsOfAreas(drawn), [drawn]);

  return (
    <div className="portal-map-wrap portal-buy-map" data-testid="buy-search-area-map">
      {bounds && (
        <MapContainer
          className="portal-map"
          bounds={bounds}
          boundsOptions={{ padding: [40, 40], maxZoom: AREA_MAP_MAX_ZOOM - 1 }}
          maxZoom={AREA_MAP_MAX_ZOOM}
          scrollWheelZoom={false}
        >
          {/* The same Esri imagery and road reference the property map draws (PortalParcelMap), stopped at the map's own zoom. */}
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Esri"
            maxZoom={AREA_MAP_MAX_ZOOM}
          />
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
            attribution="Esri"
            maxZoom={AREA_MAP_MAX_ZOOM}
          />
          {drawn.map((area) => (
            <CircleMarker
              key={area.key}
              center={[area.center.lat, area.center.lng]}
              radius={bubbleRadius(area.count)}
              pathOptions={{ color: '#fff', weight: 2, fillColor: BUBBLE_COLOR, fillOpacity: 0.6 }}
            >
              <Tooltip permanent direction="top" offset={[0, -4]} className="portal-lbl portal-area-lbl">
                {bubbleText(area)}
              </Tooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      )}
    </div>
  );
}
